// ---------------------------------------------------------------------------
// ADS-012 Phase 5 — Ad Spend Approval CQRS Event Catalog
// (vocabulary tier)
// ---------------------------------------------------------------------------
// Story ADS-012: "Campaign actions that spend money or publish externally
// require explicit user approval and audit."
//
// Phase 1 LANDED — closed vocabulary in
// `packages/db/src/schema/ads-spend-approval.ts`:
//   - `ADS_APPROVAL_STATES` (6-rung state machine)
//   - `ADS_MUTATION_KINDS` (8 spend/publish operations)
//   - `ADS_MUTATION_TARGET_KINDS` (4 operation identity groups)
//   - `ADS_APPROVAL_REJECTION_REASONS` (8 fixed-vocabulary refusal codes)
//   - `ADS_APPROVAL_EVENTS` (6-event CQRS vocabulary)
//   - `ADS_APPROVAL_TTL_HOURS = 24`
//   - state-machine guard `isLegalAdsApprovalTransition`
//
// Phase 2 LANDED 2026-05-15 — `ad_pending_mutations` persistence (migration
// 0113) with the operation-identity unique index.
//
// Phase 5 (this file): catalogue the per-event payload contracts +
// discriminated union the future Phase 3+ wiring (`requireSpendApproval`
// middleware, provider-SDK refusal guard, 24h TTL cron worker, and
// mounted spend-mutating routes) MUST emit per ADS-012-DECISION-PACKET.
//
// What this file is NOT:
//   - It does not emit events — that's the Phase 3+ middleware + cron.
//   - It does not change Phase 1 vocabulary or Phase 2 schema (additive).
//   - It does not declare a new HTTP route.
//
// Why a separate file from `ads-spend-approval.ts`: the Phase 1 file pins
// the closed vocabularies + state-machine guard + TTL. This file ADDS the
// payload shapes, discriminated union, and event-type narrowing guard
// the materializer / DLQ / audit-runner need.
// ---------------------------------------------------------------------------

import {
  ADS_APPROVAL_EVENTS,
  ADS_APPROVAL_REJECTION_REASONS,
  ADS_APPROVAL_STATES,
  ADS_APPROVAL_TTL_HOURS,
  ADS_MUTATION_KINDS,
  ADS_MUTATION_TARGET_KINDS,
  type AdsApprovalEvent,
  type AdsApprovalRejectionReason,
  type AdsApprovalState,
  type AdsMutationKind,
  type AdsMutationTargetKind,
} from "./ads-spend-approval";

/**
 * Mirror of the Phase 1 CQRS event-type vocabulary. Re-exported here so
 * downstream consumers (DLQ, audit-runner, materializer) can import the
 * event-catalog and the vocabulary from one place. Drift guard test pins
 * these to `ADS_APPROVAL_EVENTS` verbatim.
 */
export const ADS_012_SPEND_APPROVAL_EVENT_TYPES = [
  ...ADS_APPROVAL_EVENTS,
] as const;

export type Ads012SpendApprovalEventType =
  (typeof ADS_012_SPEND_APPROVAL_EVENT_TYPES)[number];

/** Re-exported Phase 1 state vocab. */
export const ADS_012_APPROVAL_STATE_VOCAB = [...ADS_APPROVAL_STATES] as const;
export type Ads012ApprovalStateVocab =
  (typeof ADS_012_APPROVAL_STATE_VOCAB)[number];

/** Re-exported Phase 1 mutation-kind vocab. */
export const ADS_012_MUTATION_KIND_VOCAB = [...ADS_MUTATION_KINDS] as const;
export type Ads012MutationKindVocab =
  (typeof ADS_012_MUTATION_KIND_VOCAB)[number];

/** Re-exported Phase 1 target-kind vocab. */
export const ADS_012_MUTATION_TARGET_KIND_VOCAB = [
  ...ADS_MUTATION_TARGET_KINDS,
] as const;
export type Ads012MutationTargetKindVocab =
  (typeof ADS_012_MUTATION_TARGET_KIND_VOCAB)[number];

/** Re-exported Phase 1 rejection-reason vocab. */
export const ADS_012_REJECTION_REASON_VOCAB = [
  ...ADS_APPROVAL_REJECTION_REASONS,
] as const;
export type Ads012RejectionReasonVocab =
  (typeof ADS_012_REJECTION_REASON_VOCAB)[number];

/** Re-exported Phase 1 TTL constant (for materializer windowing). */
export const ADS_012_APPROVAL_TTL_HOURS_VALUE = ADS_APPROVAL_TTL_HOURS;

/* -------------------------------------------------------------------------- */
/* Runtime narrowing guards                                                   */
/* -------------------------------------------------------------------------- */

/**
 * Runtime narrowing guard. DLQ consumers and the audit-runner use this to
 * reject unknown event types at the boundary instead of silently fanning
 * them downstream.
 */
export function isAds012SpendApprovalEventType(
  value: unknown,
): value is Ads012SpendApprovalEventType {
  return (
    typeof value === "string" &&
    (ADS_012_SPEND_APPROVAL_EVENT_TYPES as readonly string[]).includes(value)
  );
}

export function isAds012ApprovalStateVocab(
  value: unknown,
): value is Ads012ApprovalStateVocab {
  return (
    typeof value === "string" &&
    (ADS_012_APPROVAL_STATE_VOCAB as readonly string[]).includes(value)
  );
}

export function isAds012RejectionReasonVocab(
  value: unknown,
): value is Ads012RejectionReasonVocab {
  return (
    typeof value === "string" &&
    (ADS_012_REJECTION_REASON_VOCAB as readonly string[]).includes(value)
  );
}

/* -------------------------------------------------------------------------- */
/* Per-event payload contracts                                                */
/* -------------------------------------------------------------------------- */

/**
 * Common envelope shared by every ADS-012 event. The Phase 2 persistence
 * row (`ad_pending_mutations`) is the single source of truth — the event
 * payload mirrors the row's stable identity columns so a consumer can
 * look the row up without an extra join.
 */
export interface Ads012BaseEventPayload {
  /** Tenant scope for fan-out + isolation. */
  tenantId: string;
  /** Logical mutation identifier (`ad_pending_mutations.mutation_id`). */
  mutationId: string;
  /** Operation type. */
  mutationKind: Ads012MutationKindVocab;
  /** Operation identity grouping (matches the unique index). */
  targetKind: Ads012MutationTargetKindVocab;
  /** Provider object id (FK target — campaign id, ad set id, ...). */
  targetId: string;
  /** SHA-256 hex of the canonical change payload. Operation identity key. */
  changePayloadHash: string;
  /** ISO-8601 — when this event was emitted. */
  occurredAt: string;
}

/** Emitted when an operator submits a mutation for approval. */
export interface Ads012MutationRequestedPayload extends Ads012BaseEventPayload {
  /** Operator who requested the mutation. */
  requesterId: string;
}

/** Emitted when an admin approves a pending mutation. */
export interface Ads012MutationApprovedPayload extends Ads012BaseEventPayload {
  /** Admin who approved (must NOT equal requesterId — self-approval blocked). */
  approverId: string;
}

/** Emitted when an admin rejects a pending mutation. */
export interface Ads012MutationRejectedPayload extends Ads012BaseEventPayload {
  /** Admin who rejected. */
  approverId: string;
  /** Refusal code from the closed Phase 1 vocabulary. */
  reason: Ads012RejectionReasonVocab;
}

/** Emitted by the TTL cron worker when a pending mutation ages past 24h. */
export interface Ads012MutationExpiredPayload extends Ads012BaseEventPayload {
  /** ISO-8601 — original expiry timestamp from `ad_pending_mutations.expires_at`. */
  expiresAt: string;
}

/** Emitted when the provider SDK successfully applies an approved mutation. */
export interface Ads012MutationAppliedPayload extends Ads012BaseEventPayload {
  /** Provider's response id (e.g. Meta/Google ad object id) for traceability. */
  providerResponseId: string;
  /** ISO-8601 — when the provider SDK confirmed the apply. */
  appliedAt: string;
}

/**
 * Emitted when the provider SDK rejects an approved mutation (4xx/5xx) per
 * the Phase 2 persistence contract `state = failed_at_provider`. The
 * provider's error code is preserved in `ad_pending_mutations.provider_error_code`.
 */
export interface Ads012MutationFailedAtProviderPayload
  extends Ads012BaseEventPayload {
  /** Provider's stable error code (e.g. "permission_denied", "invalid_budget"). */
  providerErrorCode: string;
  /** Optional provider response id when one was returned. */
  providerResponseId: string | null;
}

/**
 * Discriminated union mapping each event type to its payload. Consumers
 * (analytics materializer, DLQ, audit-runner) can `switch (event.type)`
 * and pull the right payload shape without any-casting.
 */
export type Ads012SpendApprovalEvent =
  | {
      type: "ads.mutation.requested@v1";
      payload: Ads012MutationRequestedPayload;
    }
  | {
      type: "ads.mutation.approved@v1";
      payload: Ads012MutationApprovedPayload;
    }
  | {
      type: "ads.mutation.rejected@v1";
      payload: Ads012MutationRejectedPayload;
    }
  | {
      type: "ads.mutation.expired@v1";
      payload: Ads012MutationExpiredPayload;
    }
  | {
      type: "ads.mutation.applied@v1";
      payload: Ads012MutationAppliedPayload;
    }
  | {
      type: "ads.mutation.failed_at_provider@v1";
      payload: Ads012MutationFailedAtProviderPayload;
    };

// Re-export Phase 1 narrowing helpers so consumers can import everything
// from this catalog file. (Phase 1 already exports `isAdsMutationKind` +
// `isAdsMutationTargetKind`.)
export type {
  AdsApprovalEvent as Ads012Phase1EventTypeAlias,
  AdsApprovalRejectionReason as Ads012Phase1RejectionReasonAlias,
  AdsApprovalState as Ads012Phase1StateAlias,
  AdsMutationKind as Ads012Phase1KindAlias,
  AdsMutationTargetKind as Ads012Phase1TargetKindAlias,
};
